import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { fabBottomCss, fabRightCss } from '../../constants/fabLayout';

const DISMISS_KEY = 'apt_live_agent_callout_dismissed_v1';

function readDismissed(): boolean {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    if (!raw) {
      return false;
    }
    const until = Number(raw);
    if (!Number.isFinite(until)) {
      return false;
    }
    return Date.now() < until;
  } catch {
    return false;
  }
}

function writeDismissed(hours = 4): void {
  try {
    sessionStorage.setItem(DISMISS_KEY, String(Date.now() + hours * 60 * 60 * 1000));
  } catch {
    // ignore
  }
}

export interface LiveAgentAvailabilityCalloutProps {
  /** When true, a live support agent is available. */
  isLive: boolean;
  /** Hide while chat widget / speed dial menu is already open. */
  suppressed?: boolean;
  /** Extra offset above the FAB (e.g. dashboard chrome). */
  fabExtraBottomPx?: number;
  onChatNow: () => void;
}

/**
 * Compact visitor callout near the help speed dial when a live agent is online.
 */
export function LiveAgentAvailabilityCallout({
  isLive,
  suppressed = false,
  fabExtraBottomPx = 0,
  onChatNow,
}: LiveAgentAvailabilityCalloutProps): React.ReactElement | null {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    if (!isLive) {
      return;
    }
    setDismissed(readDismissed());
  }, [isLive]);

  const handleDismiss = useCallback(() => {
    writeDismissed(4);
    setDismissed(true);
  }, []);

  const handleChatNow = useCallback(() => {
    writeDismissed(0.25); // brief quiet period after opening chat
    setDismissed(true);
    onChatNow();
  }, [onChatNow]);

  if (!isLive || suppressed || dismissed) {
    return null;
  }

  const fabSize = isMobile ? 48 : 56;
  const gap = 12;

  return (
    <Paper
      elevation={6}
      role="status"
      aria-live="polite"
      data-testid="live-agent-callout"
      sx={{
        position: 'fixed',
        zIndex: (t) => t.zIndex.speedDial + 1,
        bottom: `calc(${fabBottomCss(isMobile, fabExtraBottomPx)} + ${fabSize + gap}px)`,
        right: fabRightCss(isMobile),
        width: { xs: 'min(280px, calc(100vw - 32px))', sm: 300 },
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.success.main, 0.45)}`,
        background:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.96)
            : theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 8px 24px rgba(0,0,0,0.45)'
            : '0 8px 24px rgba(0,0,0,0.12)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box
          sx={{
            mt: 0.25,
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: 'success.main',
            flexShrink: 0,
            boxShadow: `0 0 0 4px ${alpha(theme.palette.success.main, 0.25)}`,
            animation: 'aptLivePulse 1.8s ease-out infinite',
            '@keyframes aptLivePulse': {
              '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.45)}` },
              '70%': { boxShadow: `0 0 0 8px ${alpha(theme.palette.success.main, 0)}` },
              '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0)}` },
            },
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3, mb: 0.5 }}>
            A live agent is available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, lineHeight: 1.4 }}>
            Chat with a human from American Police Training — we&apos;re online now to help.
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<SupportAgentIcon />}
            onClick={handleChatNow}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Chat now
          </Button>
        </Box>
        <IconButton
          size="small"
          aria-label="Dismiss live agent notice"
          onClick={handleDismiss}
          sx={{ mt: -0.5, mr: -0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}

export default LiveAgentAvailabilityCallout;
